import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getCreditBalanceUseCase } from '@src/container';
import { useAuth } from '@src/presentation/contexts/AuthContext';

/**
 * Hook: useCreditBalance
 * Fetches the user's credit balance
 * Only enabled when user is authenticated (based on user fetch, not tokens)
 * 
 * @example
 * const { data: creditBalance, isLoading, error } = useCreditBalance();
 */
export function useCreditBalance() {
    const creditBalanceUseCase = getCreditBalanceUseCase();
    const { isAuthenticated } = useAuth();

    return useQuery({
        queryKey: ['credits', 'balance'],
        queryFn: async () => {
            const balance = await creditBalanceUseCase.execute();
            if (import.meta.env.DEV) {
                console.log('✅ Credit balance fetched:', balance);
            }
            return balance;
        },
        staleTime: 1000 * 60 * 2, // 2 minutes
        gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
        retry: 1,
        enabled: isAuthenticated, // Only fetch if user is authenticated
        refetchOnWindowFocus: true,
        refetchOnMount: false,
        refetchOnReconnect: true,
    });
}

/**
 * Hook: useRefreshCredits
 * Returns a function to manually refresh credit balance
 * Call this after actions that consume credits (chat, download, etc.)
 * 
 * @example
 * const refreshCredits = useRefreshCredits();
 * // After sending a chat message
 * await sendMessage();
 * refreshCredits();
 */
export function useRefreshCredits() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries({ queryKey: ['credits', 'balance'] });
    };
}
